import { Loader } from '@systemeio/ui'
import { useCourse } from 'modules/courses/courses/hooks/use-course'
import { LightCourseSettings } from 'modules/courses/courses/light-course/components/light-course-settings'
import { CourseEditorTypeEnum, CourseId } from 'modules/courses/courses/types/course-interface'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import ItemSteps from 'modules/stores/store/components/item-steps'
import { FC, useEffect } from 'react'
import { Link } from 'shared/components/link'
import { SectionMessage } from 'shared/components/section-message'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useCoursePreview } from '../hooks/use-course-preview'
import { useCourseCurriculum } from 'modules/courses/courses/curriculum/hooks/use-cource-curriculum'

interface Props {
  courseId?: CourseId
  confirmationEmail: DigitalProductChangeInterface['confirmationEmail']
  onChange: (data: DigitalProductChangeInterface['confirmationEmail']) => void
  errors: DigitalProductErrorInterface['confirmationEmail']
}

const Options: FC<Props> = ({ courseId, confirmationEmail, onChange, errors }) => {
  const { t } = useLocoTranslation()
  const { courseData } = useCourse(courseId)
  const { data: courseCurriculumData } = useCourseCurriculum(courseId)
  const { showCoursePreview } = useCoursePreview()

  useEffect(() => {
    if (courseId) {
      showCoursePreview(courseData, courseCurriculumData)
    }
  }, [courseId, courseData, courseCurriculumData, showCoursePreview])

  const loaderItem = {
    key: 'loader',
    title: t('dashboard.light_course.course_settings'),
    element: <Loader small />,
  }

  const emptyStateItem = {
    key: 'empty',
    title: t('dashboard.light_course.course_settings'),
    element: (
      <SectionMessage style="warn">
        {t('dashboard.light_course.select_course_to_proceed_with_settings')}
      </SectionMessage>
    ),
  }

  const advancedCourseSettingsItem = {
    key: 'loader',
    title: t('dashboard.light_course.course_settings'),
    element: (
      <Link rel="noreferrer" target={'_blank'} href={`/courses/${courseId}`} showOpenInNewIcon>
        {t('dashboard.course.go_to_change_settings')}
      </Link>
    ),
  }

  // TODO: Uncomment later
  // const confirmationEmailItem = {
  //   key: 'confirmation-email',
  //   title: t('dashboard.light_course.confirmation_email'),
  //   element: (
  //     <ConfirmationEmail
  //       confirmationEmail={confirmationEmail}
  //       onChange={onChange}
  //       errors={errors}
  //     />
  //   ),
  // }

  if (!courseId) {
    return <ItemSteps steps={[emptyStateItem]} />
  }

  if (!courseData) {
    return <ItemSteps steps={[loaderItem]} />
  }

  if (courseData.courseType === CourseEditorTypeEnum.ClassicCourse) {
    return <ItemSteps steps={[advancedCourseSettingsItem]} />
  }

  if (courseData.courseType === CourseEditorTypeEnum.LightCourse) {
    return <LightCourseSettings courseId={courseId} />
  }

  return <ItemSteps steps={[loaderItem]} />
}

export default Options
